import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api from "@/services/api";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  User,
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  ArrowLeft,
  Target,
  Wrench,
  Info,
  Search,
  FileCheck,
  Calendar,
  Award,
  Filter,
} from "lucide-react";

export default function VerificationCompetences() {
  const { id } = useParams(); // collaborateur_id
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useApp();
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedMachine, setSelectedMachine] = useState("all");
  const [selectedCategorie, setSelectedCategorie] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [grille, setGrille] = useState({});
  const [activeTab, setActiveTab] = useState("competences");

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/collaborateurs/${id}/verification-competences`);
        setData(response);
        setGrille(response.grille || {});
      } catch (error) {
        console.error("Error loading verification data:", error);
        if (error.response?.status === 404) {
          toast.error("Collaborateur non trouvé");
          navigate("/collaborateurs");
        } else {
          toast.error("Erreur lors du chargement des données");
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (!authLoading && !isAuthenticated) {
      // Redirect to login with return URL
      toast.warning("Vous devez être connecté pour accéder à cette page");
      navigate(`/login?redirect=/verification-competences/${id}`);
      return;
    }
    
    if (isAuthenticated) {
      loadData();
    }
  }, [id, isAuthenticated, authLoading, navigate]);

  // Get unique categories from machines
  const categoriesMachines = useMemo(() => {
    if (!data?.verifications) return [];
    const cats = [...new Set(data.verifications.map(v => v.machine_categorie).filter(Boolean))];
    return cats.sort();
  }, [data]);

  // Filter verifications based on search, selected machine and category
  const filteredVerifications = useMemo(() => {
    if (!data?.verifications) return [];
    
    return data.verifications.filter(v => {
      // Filter by machine
      if (selectedMachine !== "all" && v.machine_id !== selectedMachine) return false;
      
      // Filter by category
      if (selectedCategorie !== "all" && v.machine_categorie !== selectedCategorie) return false;
      
      // Filter by search term
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchName = v.machine_nom?.toLowerCase().includes(search);
        const matchCode = v.machine_code?.toLowerCase().includes(search);
        const matchCat = v.machine_categorie?.toLowerCase().includes(search);
        if (!matchName && !matchCode && !matchCat) return false;
      }
      
      return true;
    });
  }, [data, selectedMachine, selectedCategorie, searchTerm]);

  // Get unique machines for filter
  const machines = useMemo(() => {
    if (!data?.verifications) return [];
    return data.verifications.map(v => ({
      id: v.machine_id,
      nom: v.machine_nom,
      code: v.machine_code,
      categorie: v.machine_categorie
    }));
  }, [data]);

  // Autorisations valides
  const autorisations = useMemo(() => {
    return data?.autorisations || [];
  }, [data]);

  const getStatusIcon = (statut) => {
    switch (statut) {
      case "autorise":
        return <CheckCircle className="h-6 w-6 text-green-500" />;
      case "non_autorise":
        return <XCircle className="h-6 w-6 text-red-500" />;
      case "aucune_exigence":
        return <Info className="h-6 w-6 text-gray-400" />;
      default:
        return <AlertTriangle className="h-6 w-6 text-yellow-500" />;
    }
  };

  const getStatusBadge = (statut, label) => {
    switch (statut) {
      case "autorise":
        return <Badge className="bg-green-100 text-green-800">{label}</Badge>;
      case "non_autorise":
        return <Badge className="bg-red-100 text-red-800">{label}</Badge>;
      case "aucune_exigence":
        return <Badge variant="outline">{label}</Badge>;
      default:
        return <Badge variant="outline">{label}</Badge>;
    }
  };

  const getNiveauColor = (niveau) => {
    return grille[niveau]?.couleur || "#gray";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("fr-FR");
    } catch {
      return dateStr;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="space-y-6" data-testid="verification-competences-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="h-7 w-7 text-primary" />
              Vérification Collaborateur
            </h1>
            <p className="text-gray-600 mt-1">
              Compétences machines et autorisations de conduite
            </p>
          </div>
        </div>
      </div>

      {/* Collaborateur Info */}
      {data?.collaborateur && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="h-8 w-8 text-blue-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold">
                  {data.collaborateur.prenom} {data.collaborateur.nom}
                </h2>
                <p className="text-gray-500">Matricule: {data.collaborateur.matricule}</p>
                {data.collaborateur.poste && (
                  <p className="text-sm text-gray-400">{data.collaborateur.poste}</p>
                )}
              </div>
              <div className="text-right">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{autorisations.length}</p>
                    <p className="text-xs text-gray-500">Autorisation(s) valide(s)</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{machines.length}</p>
                    <p className="text-xs text-gray-500">Machine(s) à vérifier</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs for Competences, Formations and Autorisations */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="competences" className="gap-2">
            <Wrench className="h-4 w-4" />
            Compétences Machines ({machines.length})
          </TabsTrigger>
          <TabsTrigger value="formations" className="gap-2">
            <Award className="h-4 w-4" />
            Formations ({data?.formations?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="autorisations" className="gap-2">
            <FileCheck className="h-4 w-4" />
            Autorisations ({autorisations.length})
          </TabsTrigger>
        </TabsList>

        {/* Competences Tab */}
        <TabsContent value="competences" className="space-y-4">
          {/* Search and Filters */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="h-5 w-5" />
                Rechercher une machine
              </CardTitle>
              <CardDescription>
                Filtrez par nom, code ou secteur pour trouver rapidement une machine
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher par nom ou code..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="search-machine-input"
                  />
                </div>
                <Select value={selectedCategorie} onValueChange={setSelectedCategorie}>
                  <SelectTrigger className="w-full sm:w-56" data-testid="filter-categorie">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Secteur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les secteurs</SelectItem>
                    {categoriesMachines.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={selectedMachine} onValueChange={setSelectedMachine}>
                  <SelectTrigger className="w-full sm:w-64" data-testid="machine-select">
                    <Wrench className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Machine spécifique" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les machines</SelectItem>
                    {machines.map((machine) => (
                      <SelectItem key={machine.id} value={machine.id}>
                        {machine.nom} {machine.code && `(${machine.code})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(searchTerm || selectedCategorie !== "all" || selectedMachine !== "all") && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-sm text-gray-500">
                    {filteredVerifications.length} machine(s) trouvée(s)
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedCategorie("all");
                      setSelectedMachine("all");
                    }}
                  >
                    Réinitialiser
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results */}
          {filteredVerifications.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Wrench className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">
                  {searchTerm || selectedCategorie !== "all" || selectedMachine !== "all"
                    ? "Aucune machine trouvée avec ces critères"
                    : "Aucune machine configurée"}
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  {!searchTerm && selectedCategorie === "all" && selectedMachine === "all"
                    ? "Contactez un administrateur pour configurer les machines et leurs compétences requises."
                    : "Essayez de modifier vos critères de recherche."}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredVerifications.map((verification) => (
                <Card 
                  key={verification.machine_id}
                  className={`border-l-4 ${
                    verification.statut === "autorise" 
                      ? "border-l-green-500" 
                      : verification.statut === "non_autorise"
                      ? "border-l-red-500"
                      : "border-l-gray-300"
                  }`}
                  data-testid={`verification-${verification.machine_id}`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(verification.statut)}
                        <div>
                          <CardTitle className="text-lg">{verification.machine_nom}</CardTitle>
                          <div className="flex items-center gap-2">
                            {verification.machine_code && (
                              <CardDescription>{verification.machine_code}</CardDescription>
                            )}
                            {verification.machine_categorie && (
                              <Badge variant="outline" className="text-xs">
                                {verification.machine_categorie}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      {getStatusBadge(verification.statut, verification.statut_label)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {verification.nb_total_competences_requises === 0 && verification.nb_total_formations_requises === 0 ? (
                      <p className="text-sm text-gray-500">
                        Cette machine n&apos;a pas de compétences ni de formations requises configurées.
                      </p>
                    ) : (
                      <div className="space-y-4">
                        {/* Summary */}
                        <div className="flex flex-wrap items-center gap-4 text-sm">
                          {verification.nb_total_competences_requises > 0 && (
                            <span className="text-gray-500">
                              Compétences validées: {verification.nb_competences_validees}/{verification.nb_total_competences_requises}
                            </span>
                          )}
                          {verification.nb_total_formations_requises > 0 && (
                            <span className="text-gray-500">
                              Formations validées: {verification.nb_formations_validees}/{verification.nb_total_formations_requises}
                            </span>
                          )}
                          {verification.competences_manquantes.length > 0 && (
                            <Badge variant="destructive" className="text-xs">
                              {verification.competences_manquantes.length} compétence(s) manquante(s)
                            </Badge>
                          )}
                          {verification.competences_insuffisantes.length > 0 && (
                            <Badge className="bg-orange-100 text-orange-800 text-xs">
                              {verification.competences_insuffisantes.length} insuffisante(s)
                            </Badge>
                          )}
                          {verification.formations_manquantes.length > 0 && (
                            <Badge variant="destructive" className="text-xs">
                              {verification.formations_manquantes.length} formation(s) manquante(s)
                            </Badge>
                          )}
                        </div>

                        {/* Competences OK */}
                        {verification.competences_ok.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-green-700 mb-2 flex items-center gap-1">
                              <CheckCircle className="h-4 w-4" /> Compétences validées
                            </p>
                            <div className="grid gap-2">
                              {verification.competences_ok.map((comp) => (
                                <div 
                                  key={comp.competence_id}
                                  className="flex items-center justify-between p-2 bg-green-50 rounded-lg"
                                >
                                  <span className="text-sm">{comp.competence_nom}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">
                                      Requis: {comp.niveau_requis}
                                    </span>
                                    <div
                                      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                      style={{ backgroundColor: getNiveauColor(comp.niveau_collaborateur) }}
                                    >
                                      {comp.niveau_collaborateur}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Competences insuffisantes */}
                        {verification.competences_insuffisantes.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-orange-700 mb-2 flex items-center gap-1">
                              <AlertTriangle className="h-4 w-4" /> Niveau insuffisant
                            </p>
                            <div className="grid gap-2">
                              {verification.competences_insuffisantes.map((comp) => (
                                <div 
                                  key={comp.competence_id}
                                  className="flex items-center justify-between p-2 bg-orange-50 rounded-lg"
                                >
                                  <span className="text-sm">{comp.competence_nom}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">
                                      Requis: {comp.niveau_requis}
                                    </span>
                                    <div
                                      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                      style={{ backgroundColor: getNiveauColor(comp.niveau_collaborateur) }}
                                    >
                                      {comp.niveau_collaborateur}
                                    </div>
                                    <span className="text-xs text-orange-600">
                                      (manque {comp.niveau_requis - comp.niveau_collaborateur} niveau(x))
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Competences manquantes */}
                        {verification.competences_manquantes.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                              <XCircle className="h-4 w-4" /> Compétences manquantes
                            </p>
                            <div className="grid gap-2">
                              {verification.competences_manquantes.map((comp) => (
                                <div 
                                  key={comp.competence_id}
                                  className="flex items-center justify-between p-2 bg-red-50 rounded-lg"
                                >
                                  <span className="text-sm">{comp.competence_nom}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">
                                      Requis: niveau {comp.niveau_requis}
                                    </span>
                                    <Badge variant="destructive" className="text-xs">
                                      Non évalué
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Formations OK */}
                        {verification.formations_ok && verification.formations_ok.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-green-700 mb-2 flex items-center gap-1">
                              <CheckCircle className="h-4 w-4" /> Formations validées
                            </p>
                            <div className="grid gap-2">
                              {verification.formations_ok.map((formation) => (
                                <div 
                                  key={formation.formation_id}
                                  className="flex items-center justify-between p-2 bg-green-50 rounded-lg"
                                >
                                  <span className="text-sm">{formation.formation_nom}</span>
                                  <div className="flex items-center gap-2">
                                    {formation.date_fin && (
                                      <span className="text-xs text-gray-500 flex items-center gap-1">
                                        <Calendar className="h-3 w-3" />
                                        Valide jusqu&apos;au {formatDate(formation.date_fin)}
                                      </span>
                                    )}
                                    <Badge className="bg-green-100 text-green-800 text-xs">
                                      ✓ Valide
                                    </Badge>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Formations manquantes */}
                        {verification.formations_manquantes && verification.formations_manquantes.length > 0 && (
                          <div>
                            <p className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                              <XCircle className="h-4 w-4" /> Formations manquantes ou expirées
                            </p>
                            <div className="grid gap-2">
                              {verification.formations_manquantes.map((formation) => (
                                <div 
                                  key={formation.formation_id}
                                  className="flex items-center justify-between p-2 bg-red-50 rounded-lg"
                                  data-testid={`formation-manquante-${formation.formation_id}`}
                                >
                                  <span className="text-sm font-medium">{formation.formation_nom}</span>
                                  <Badge variant="destructive" className="text-xs">
                                    ❌ Requise
                                  </Badge>
                                </div>
                              ))}
                            </div>
                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-red-800">
                                  <p className="font-medium">⚠️ Accès refusé</p>
                                  <p className="mt-1">
                                    Le collaborateur ne peut pas utiliser cet équipement tant que 
                                    les formations requises ne sont pas validées.
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Legend */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Légende des niveaux</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {Object.entries(grille).map(([niveau, info]) => (
                  <div key={niveau} className="flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: info.couleur }}
                    >
                      {niveau}
                    </div>
                    <span className="text-sm text-gray-600">{info.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Formations Tab */}
        <TabsContent value="formations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5 text-blue-600" />
                Formations en Cours de Validité
              </CardTitle>
              <CardDescription>
                Liste de toutes les formations valides suivies par ce collaborateur
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!data?.formations || data.formations.length === 0 ? (
                <div className="text-center py-12">
                  <Award className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aucune formation enregistrée</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Ce collaborateur n&apos;a pas encore de formations enregistrées dans le système.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {data.formations.map((formation) => {
                    const isValid = formation.est_valide !== false;
                    const joursRestants = formation.jours_restants;
                    const isExpiringSoon = joursRestants !== null && joursRestants <= 90 && joursRestants > 0;
                    
                    return (
                      <Card 
                        key={formation.id} 
                        className={`border-l-4 ${
                          !isValid 
                            ? "border-l-red-500" 
                            : isExpiringSoon 
                            ? "border-l-orange-500" 
                            : "border-l-green-500"
                        }`}
                        data-testid={`formation-${formation.id}`}
                      >
                        <CardContent className="pt-4">
                          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                            <div className="space-y-2 flex-1">
                              <div className="flex items-start gap-3">
                                <Award className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                                  !isValid 
                                    ? "text-red-500" 
                                    : isExpiringSoon 
                                    ? "text-orange-500" 
                                    : "text-green-500"
                                }`} />
                                <div className="flex-1">
                                  <h3 className="font-semibold text-lg">
                                    {formation.type_formation_intitule || formation.intitule}
                                  </h3>
                                  {formation.type_formation_code && (
                                    <p className="text-sm text-gray-500">
                                      Code: {formation.type_formation_code}
                                    </p>
                                  )}
                                </div>
                                {!isValid ? (
                                  <Badge variant="destructive" className="gap-1">
                                    <XCircle className="h-3 w-3" />
                                    Expirée
                                  </Badge>
                                ) : isExpiringSoon ? (
                                  <Badge className="bg-orange-100 text-orange-800 gap-1">
                                    <AlertTriangle className="h-3 w-3" />
                                    Expire bientôt
                                  </Badge>
                                ) : (
                                  <Badge className="bg-green-100 text-green-800 gap-1">
                                    <CheckCircle className="h-3 w-3" />
                                    Valide
                                  </Badge>
                                )}
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm ml-8">
                                <div className="flex items-center gap-2 text-gray-600">
                                  <Calendar className="h-4 w-4" />
                                  <span>Formation: {formatDate(formation.date_formation)}</span>
                                </div>
                                {formation.date_fin && (
                                  <div className={`flex items-center gap-2 ${
                                    !isValid ? "text-red-600" : isExpiringSoon ? "text-orange-600" : "text-gray-600"
                                  }`}>
                                    <Calendar className="h-4 w-4" />
                                    <span>Expiration: {formatDate(formation.date_fin)}</span>
                                  </div>
                                )}
                                {formation.organisme && (
                                  <div className="flex items-center gap-2 text-gray-600">
                                    <Target className="h-4 w-4" />
                                    <span>{formation.organisme}</span>
                                  </div>
                                )}
                                {formation.numero_attestation && (
                                  <div className="flex items-center gap-2 text-gray-600">
                                    <FileCheck className="h-4 w-4" />
                                    <span className="font-mono text-xs">
                                      {formation.numero_attestation}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Jours restants */}
                              {isValid && joursRestants !== null && (
                                <div className="ml-8 mt-2">
                                  {isExpiringSoon ? (
                                    <div className="flex items-start gap-2 p-2 bg-orange-50 border border-orange-200 rounded-lg">
                                      <AlertTriangle className="h-4 w-4 text-orange-600 flex-shrink-0 mt-0.5" />
                                      <div className="text-sm text-orange-800">
                                        <span className="font-medium">
                                          Expire dans {joursRestants} jour{joursRestants > 1 ? 's' : ''}
                                        </span>
                                        <p className="text-xs mt-1">
                                          Pensez à planifier le renouvellement de cette formation
                                        </p>
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="text-xs text-gray-500">
                                      Valide encore {joursRestants} jour{joursRestants > 1 ? 's' : ''}
                                    </p>
                                  )}
                                </div>
                              )}

                              {/* Formation expirée */}
                              {!isValid && (
                                <div className="ml-8 mt-2">
                                  <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                                    <XCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                                    <div className="text-sm text-red-800">
                                      <span className="font-medium">Formation expirée</span>
                                      <p className="text-xs mt-1">
                                        Cette formation doit être renouvelée pour rester valide
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">À propos des formations</p>
                  <p className="mt-1">
                    Les formations affichées ici sont toutes celles enregistrées pour ce collaborateur, 
                    qu&apos;elles soient valides ou expirées. Les formations expirées doivent être 
                    renouvelées pour pouvoir utiliser les équipements qui les requièrent.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Autorisations Tab */}
        <TabsContent value="autorisations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="h-5 w-5 text-green-600" />
                Autorisations de Conduite Valides
              </CardTitle>
              <CardDescription>
                Liste des autorisations de conduite actuellement valides pour ce collaborateur
              </CardDescription>
            </CardHeader>
            <CardContent>
              {autorisations.length === 0 ? (
                <div className="text-center py-12">
                  <FileCheck className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aucune autorisation de conduite valide</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Ce collaborateur n&apos;a pas d&apos;autorisation de conduite active à ce jour.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {autorisations.map((auth) => (
                    <Card key={auth.id} className="border-l-4 border-l-green-500">
                      <CardContent className="pt-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <Shield className="h-5 w-5 text-green-600" />
                              <span className="font-mono font-medium text-lg">{auth.numero}</span>
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Valide
                              </Badge>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {(auth.categories_caces_codes || []).map((cat, idx) => (
                                <Badge key={idx} variant="outline" className="gap-1">
                                  <Award className="h-3 w-3" />
                                  {cat}
                                </Badge>
                              ))}
                            </div>
                            {auth.categories_caces_libelles && auth.categories_caces_libelles.length > 0 && (
                              <p className="text-sm text-gray-500">
                                {auth.categories_caces_libelles.join(", ")}
                              </p>
                            )}
                          </div>
                          <div className="flex flex-col sm:items-end gap-1 text-sm">
                            <div className="flex items-center gap-2 text-gray-600">
                              <Calendar className="h-4 w-4" />
                              <span>Du {formatDate(auth.date_debut)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <Calendar className="h-4 w-4" />
                              <span>Au {formatDate(auth.date_fin)}</span>
                            </div>
                            {auth.signe_date && (
                              <p className="text-xs text-gray-400">
                                Délivrée le {formatDate(auth.signe_date)}
                              </p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">À propos des autorisations</p>
                  <p className="mt-1">
                    Seules les autorisations avec le statut &quot;Délivrée&quot; et une date de fin de validité 
                    postérieure à aujourd&apos;hui sont affichées ici. Pour gérer les autorisations, 
                    rendez-vous dans la section &quot;Autorisations de Conduite&quot;.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
